import type {
  CapturedEvent,
  CapturedEventInput,
  InitResult,
  SnowplowClient,
  TrackResult,
} from '@nextmar/snowplow-sdk';

/** App 初始化期间的页内快照容量，超限保留最新事件，不持久化或网络重试。 */
const MAX_INITIALIZING_SNAPSHOTS = 1000;
/** 仅由 facade 持有的冻结句柄，业务 UUID 只用于注册去重。 */
interface AppSnapshot {
  event: CapturedEvent;
  signUpUuid?: string;
}

/**
 * 承接 App 原有 initializing 接纳能力，SDK 自身不再保存初始化队列。
 * @param actual 同一页面唯一真实 WebView Client；所有桥发送仍由它处理。
 * @returns 同合同 facade；idle 拒绝，初始化期间冻结 FIFO，排空后才宣布 ready。
 */
export function createAppInitializingClient(actual: SnowplowClient): SnowplowClient {
  const snapshots: AppSnapshot[] = [];
  const signUpClaims = new Set<string>();
  let initialization: Promise<InitResult> | undefined;
  let initializing = false;
  let generation = 0;
  let autoClickReferences = 0;
  let releaseAutoClick: (() => void) | undefined;

  /** 释放当前代暂存句柄，不撤回已交给 bridge 的事件。
   * @returns 无返回值；无入参，清除本代注册占用。
   */
  function clearSnapshots(): void {
    for (const record of snapshots.splice(0)) actual.cancelCapturedEvent(record.event);
    signUpClaims.clear();
  }

  /** 只在真正 initializing 或其排空窗口接纳页内快照。
   * @returns 当前处于初始化接纳窗口时为 true；无入参。
   */
  function shouldCapture(): boolean {
    const status = actual.getState().status;
    return initializing && (status === 'initializing' || status === 'ready');
  }

  /**
   * 用真实 SDK 捕获并校验一个初始化事件，不调用 Tracker。
   * @param input 保留原生分类和业务参数的触发输入。
   * @returns 本页接纳结果；注册参数非法与重复在接纳时拒绝。
   */
  function capture(input: CapturedEventInput): TrackResult {
    try {
      const signUpUuid =
        input.kind === 'signUp' && typeof input.user.uuid === 'string'
          ? input.user.uuid.trim()
          : undefined;
      if (signUpUuid && signUpClaims.has(signUpUuid))
        return { accepted: false, reason: 'duplicate' };
      const result = actual.captureEvent(input, { identityPhase: 'established' });
      if (result.captured === false)
        return {
          accepted: false,
          reason:
            result.reason === 'disabled' || result.reason === 'destroyed'
              ? result.reason
              : 'invalid_event',
        };
      if (snapshots.length >= MAX_INITIALIZING_SNAPSHOTS) {
        const dropped = snapshots.shift()!;
        actual.cancelCapturedEvent(dropped.event);
        if (dropped.signUpUuid) signUpClaims.delete(dropped.signUpUuid);
      }
      snapshots.push({ event: result.event, signUpUuid });
      if (signUpUuid) signUpClaims.add(signUpUuid);
      return { accepted: true };
    } catch {
      return { accepted: false, reason: 'runtime_error' };
    }
  }

  /**
   * 初始化真实 Client，然后按 FIFO 提交本代所有快照。
   * @returns 排空完成后的稳定初始化 Promise；失败/销毁清空本代且不重放，无入参。
   */
  function init(): Promise<InitResult> {
    if (initializing && initialization) return initialization;
    const status = actual.getState().status;
    if (status === 'ready' || status === 'disabled')
      return initialization ?? (initialization = actual.init());
    if (status === 'destroyed') return actual.init();
    const currentGeneration = ++generation;
    initializing = true;
    initialization = actual
      .init()
      .then(async (result) => {
        if (generation !== currentGeneration)
          return { status: 'failed', reason: 'runtime_error' } as const;
        if (result.status !== 'ready') {
          clearSnapshots();
          initializing = false;
          return result;
        }
        while (snapshots.length && generation === currentGeneration) {
          const record = snapshots.shift()!;
          const delivered = await actual.submitCapturedEvent(record.event);
          if (delivered.status === 'rejected' && record.signUpUuid)
            signUpClaims.delete(record.signUpUuid);
          if (delivered.status === 'pending') {
            actual.cancelCapturedEvent(record.event);
            clearSnapshots();
            initializing = false;
            return { status: 'failed', reason: 'runtime_error' } as const;
          }
        }
        initializing = false;
        return generation === currentGeneration
          ? result
          : ({ status: 'failed', reason: 'runtime_error' } as const);
      })
      .catch(() => {
        if (generation === currentGeneration) {
          clearSnapshots();
          initializing = false;
        }
        return { status: 'failed', reason: 'runtime_error' } as const;
      });
    return initialization;
  }

  return {
    ...actual,
    init,
    /** 普通事件参数冻结后由 initializing facade 接纳，ready 直接调用真实 SDK。
     * @param eventName 业务事件名称。
     * @param properties 可选业务属性。
     * @param options 可选事件配对和像素配置。
     * @returns 本页接纳或真实 SDK 提交结果。
     */
    track(eventName, properties, options) {
      return shouldCapture()
        ? capture({ kind: 'track', eventName, properties, options })
        : actual.track(eventName, properties, options);
    },
    /** 原生 PageView 的初始化快照保留页面与首访语义。
     * @param properties 可选页面业务属性。
     * @returns 页内接纳或真实 SDK 原生 PageView 结果。
     */
    trackPageView(properties) {
      return shouldCapture()
        ? Promise.resolve(capture({ kind: 'pageView', properties }))
        : actual.trackPageView(properties);
    },
    /** 点击数据仍由 SDK 校验，不能在桥就绪后重新读取 DOM。
     * @param properties 点击元素属性。
     * @returns 页内接纳或真实 SDK 提交结果。
     */
    trackWebClick(properties) {
      return shouldCapture()
        ? capture({ kind: 'webClick', properties })
        : actual.trackWebClick(properties);
    },
    /** 曝光数据沿用 SDK 专用合同。
     * @param properties 曝光元素属性。
     * @returns 页内接纳或真实 SDK 提交结果。
     */
    trackWebExp(properties) {
      return shouldCapture()
        ? capture({ kind: 'webExp', properties })
        : actual.trackWebExp(properties);
    },
    /** 注册在接纳时去重，身份单纯缺失仍由 SDK 告警后提交。
     * @param user 注册 UUID 与业务客户 ID。
     * @returns 注册接纳或真实 SDK 提交结果。
     */
    trackSignUp(user) {
      return shouldCapture()
        ? Promise.resolve(capture({ kind: 'signUp', user }))
        : actual.trackSignUp(user);
    },
    /** App 自动点击复用 SDK 捕获监听，初始化期间加入页内快照，ready 后直接提交。
     * @returns 幂等释放函数；无入参，多订阅共享一个监听。
     */
    startAutoClickTracking() {
      if (!autoClickReferences)
        releaseAutoClick = actual.startAutoClickCapture(
          (result) => {
            if (!result.captured) return;
            if (shouldCapture()) {
              if (snapshots.length >= MAX_INITIALIZING_SNAPSHOTS) {
                const dropped = snapshots.shift()!;
                actual.cancelCapturedEvent(dropped.event);
                if (dropped.signUpUuid) signUpClaims.delete(dropped.signUpUuid);
              }
              snapshots.push({ event: result.event });
            } else if (actual.getState().status === 'ready') {
              void actual.submitCapturedEvent(result.event);
            } else actual.cancelCapturedEvent(result.event);
          },
          () => ({ identityPhase: 'established' }),
        );
      autoClickReferences++;
      let disposed = false;
      return () => {
        if (disposed) return;
        disposed = true;
        autoClickReferences = Math.max(0, autoClickReferences - 1);
        if (!autoClickReferences) {
          releaseAutoClick?.();
          releaseAutoClick = undefined;
        }
      };
    },
    /** 排空未完成时不得提前向框架暴露 ready。
     * @returns 当前只读 Client 状态；无入参，不暴露队列长度。
     */
    getState() {
      const state = actual.getState();
      return initializing && state.status === 'ready'
        ? Object.freeze({ ...state, status: 'initializing' as const })
        : state;
    },
    /** 销毁撤销代际和所有页内引用，真实桥调用不跨通道重试。
     * @returns 无返回值；无入参，重复调用安全。
     */
    destroy() {
      generation++;
      initializing = false;
      releaseAutoClick?.();
      releaseAutoClick = undefined;
      autoClickReferences = 0;
      clearSnapshots();
      actual.destroy();
    },
  };
}
